/**
 * Commute Section Switch
 * 
 * Control: Current implementation (TravelTime component)
 * Variation: New design using ContentSectionTemplate
 */

import { useTranslation } from 'next-i18next';
import { Box } from '@mui/material';
import dynamic from 'next/dynamic';
import { AllowedExperimentKey } from '@tenants/types';
import { LOCALE_LISTING_PATHS, Namespaces } from 'types/locales';
import { ExperimentalSection } from 'ui/experimental-section';
import { ContentSectionTemplate } from 'components/atomic/templates/content-section';
import LoadingText from 'components/loading-text';
import type { StartingAddress } from 'components/pages/address/public/components/travel-time';

const TravelTime = dynamic(
  () => import('components/pages/address/public/components/travel-time'),
  { loading: () => <LoadingText /> }
);

// ============================================================================
// Types
// ============================================================================

export interface CommuteSectionProps {
  /** Section ID for scroll targeting */
  id: string;
  /** Address/listing data for travel time calculation */
  address: StartingAddress;
}

// ============================================================================
// Control (Current Implementation)
// ============================================================================

function CommuteSectionControl({ id, address }: CommuteSectionProps) {
  return (
    <Box component="section" id={id} sx={{ scrollMarginTop: 'var(--scroll-offset, 150px)' }}>
      <TravelTime address={address} />
    </Box>
  );
}

// ============================================================================
// Variation (New Design with Templates)
// ============================================================================

function CommuteSectionVariation({ id, address }: CommuteSectionProps) {
  const { t } = useTranslation(Namespaces.LISTING);

  return (
    <ContentSectionTemplate id={id} heading={t(LOCALE_LISTING_PATHS.TAB_COMMUTE)}>
      <TravelTime address={address} />
    </ContentSectionTemplate>
  );
}

// ============================================================================
// Switch
// ============================================================================

export function CommuteSectionSwitch(props: CommuteSectionProps) {
  return (
    <ExperimentalSection
      experimentKey={AllowedExperimentKey.STICKY_HEADER_TABS}
      control={<CommuteSectionControl {...props} />}
      variation={<CommuteSectionVariation {...props} />}
    />
  );
}
